import type { RepresentationData } from '@ardoq/data-model';
import type { ComponentMatrix2Config } from './types';

export const EMBEDDED_COMPONENT_MATRIX_2_CONFIG: ComponentMatrix2Config = {
  isPresentationMode: false,
};

/** Used for `REFERENCED_COMPONENT` cells whose anchor id is missing from
 * `enhancedScopeData.componentsById` (e.g. cross-workspace traversals that
 * load adjacency without full attributes). Matches the legacy
 * `componentInterface.getRepresentationData(id) ?? { ... }` fallback. */
export const DEFAULT_REPRESENTATION_DATA: RepresentationData = {
  isImage: false,
  value: null,
};

export {
  COMPONENT_HIERARCHY_PADDING,
  COMPONENT_CONTEXT_ITEM_CONTAINER_BORDER,
  COMPONENT_ITEM_CONTAINER_MARGIN,
  COMPONENT_ITEM_CONTAINER_PADDINGRIGHT,
  HEADER_CELL_HORIZONTAL_PADDING,
  HEADER_CELL_VERTICAL_PADDING,
  HEADER_ROW_HEIGHT,
  ICON_MARGIN,
  INCOMING_SUFFIX,
  LEVEL_HEADER_CELL_HORIZONTAL_PADDING,
  LEVEL_HEADER_CELL_VERTICAL_PADDING,
  MAIN_GRID_LEFT_COLUMN_MAX_WIDTH_VIEWPORT_UNITS,
  MARGIN_PER_LEVEL,
  NONE_HEADER_LABEL,
  OUTGOING_SUFFIX,
  ROW_HEADER_HIERARCHY_INDENTATION,
  ROW_HEADER_LABEL_HEIGHT,
} from '../consts';

/** Item dimensions for the new viewpoint-mode design: 4px padding + 20px line-height + 4px padding = 28px */
export const COMPONENT_ITEM_HEIGHT = 28;
/** Row header stripe height: 8px padding + 20px icon + 8px padding = 36px. In practice the flex layout renders slightly taller (~41px); use 42px to avoid clipping. */
export const ROW_HEADER_STRIPE_HEIGHT = 42;
/** 20px icon (no separate margin — replaced by flex gap + container padding) */
export const ICON_WIDTH = 20;
/** Outward space the context-highlight box-shadow on `ComponentItemContainer`
 * occupies (2px grey100 gap + 3px blue600 = 5px outward, per Figma node
 * 9329:24091). Applied as `margin` on the context item itself so non-context
 * items don't reserve any space, and factored into the body-cell width/height
 * measurement so the row and column that contain the context item grow by
 * `2 * CONTEXT_OUTLINE_OUTSET` to host the outline. */
export const CONTEXT_OUTLINE_OUTSET = 5;
/** Horizontal padding inside the gray body cell container (Figma spec: px-4px) */
export const BODY_CELL_CONTAINER_HORIZONTAL_PADDING = 4;
/** Vertical padding inside the gray body cell container (Figma spec: py-6px) */
export const BODY_CELL_CONTAINER_VERTICAL_PADDING = 6;
/** Horizontal margin on each side of the gray body cell container — creates visible gap between adjacent column stripes */
export const BODY_CELL_CONTAINER_HORIZONTAL_MARGIN = 4;

/** Item width without label text: 2*4px container margin + 2*4px container horizontal padding + 8px item left padding + 20px icon + 4px gap + 8px item right padding */
export const ITEM_WIDTH_WITHOUT_TEXT =
  2 * BODY_CELL_CONTAINER_HORIZONTAL_MARGIN +
  2 * BODY_CELL_CONTAINER_HORIZONTAL_PADDING +
  8 +
  20 +
  4 +
  8;

/** Gap above first card in a group — must match measureRowHeaders */
export const CARD_GROUP_TOP_MARGIN = 8;
/** Gap between sibling cards — must match measureRowHeaders */
export const CARD_SIBLING_GAP = 4;
/** Gap below last card in a group — must match measureRowHeaders */
export const CARD_GROUP_BOTTOM_MARGIN = 8;
/** Horizontal margin per nesting level for row header cards (Figma: 8px padding on all sides).
 * Must match the width calculation in measureRowHeaders. */
export const CARD_GROUP_HORIZONTAL_MARGIN = 8;

/** Minimum height for any header cell (row or column) per Figma spec. */
export const MIN_HEADER_CELL_HEIGHT = 44;
/** Column header card stripe height. Must be ≥ MIN_HEADER_CELL_HEIGHT and must match getColumnHeaderLevelHeights in measureCells. */
export const COLUMN_HEADER_STRIPE_HEIGHT = MIN_HEADER_CELL_HEIGHT;
/** Level header card height: measured at 53px in browser (ColumnLevelHeaderCell has no padding). */
export const LEVEL_HEADER_CARD_HEIGHT = 53;
/** Horizontal padding (both sides combined) of the LevelHeadersContainer
 * (padding: 0 left + s4 = 4px right) — provides the 4px half of the
 * 8px Figma gap between the level header and the first column header. */
export const LEVEL_HEADERS_CONTAINER_HORIZONTAL_PADDING = 4;
/** Gap between level header cards in the LevelHeadersContainer (gap: s8 = 8px). */
export const LEVEL_HEADERS_CARD_GAP = 8;
/** Minimum width of a level header card — matches min-width in LevelLabelCard styled component. */
export const LEVEL_HEADER_CARD_MIN_WIDTH = 112;
/** Maximum width of a level header card — matches max-width in LevelLabelCard styled component. */
export const LEVEL_HEADER_CARD_MAX_WIDTH = 252;
/** Maximum width of the row headers area. */
export const MAX_ROW_HEADER_WIDTH = 480;
/** Horizontal gap between sibling child cards — mirrors the Figma body container (8px gap) */
export const CARD_COLUMN_SIBLING_GAP = 8;
/** Left margin on first child in a group — mirrors the Figma body container (8px padding) */
export const CARD_COLUMN_GROUP_LEFT_MARGIN = 8;
/** Right margin on last child in a group — mirrors the Figma body container (8px padding) */
export const CARD_COLUMN_GROUP_RIGHT_MARGIN = 8;
/** Top margin on child cards — mirrors the Figma body container (8px padding) */
export const CARD_COLUMN_GROUP_TOP_MARGIN = 8;
/** Bottom margin on child cards — mirrors the Figma body container (8px padding) */
export const CARD_COLUMN_GROUP_BOTTOM_MARGIN = 8;
/** Vertical gap between root-level row groups in the body and row header grids. */
export const ROW_GROUP_VERTICAL_MARGIN = 8;
/** Line-height of component labels in CM2 (ComponentItemContainer, CardContent, CardStripe).
 * Used by measurement code to convert wrapped-line counts to pixel heights and
 * by the link icon offset to optically center the icon on the first text line. */
export const LABEL_LINE_HEIGHT = 20;

/** Minimum width of a column header cell. */
export const MIN_COLUMN_HEADER_WIDTH = 36;
/** Maximum width of a column header cell. */
export const MAX_COLUMN_HEADER_WIDTH = 496;

/**
 * CSS font shorthand for body text (text2): 400 weight, 14px/20px, Inter stack.
 * Used with canvasLabelMeasurer to measure component and list-value labels.
 */
export const COMPONENT_MATRIX_LABEL_FONT =
  '400 14px/20px Inter, -apple-system, Segoe UI, Roboto, Noto Sans, Ubuntu, Cantarell, Helvetica Neue';
/**
 * CSS font shorthand for bold body text (text2Bold): 600 weight, 14px/20px, Inter stack.
 * Used for list-value column headers which apply font-weight: 600.
 */
export const COMPONENT_MATRIX_LABEL_FONT_BOLD =
  '600 14px/20px Inter, -apple-system, Segoe UI, Roboto, Noto Sans, Ubuntu, Cantarell, Helvetica Neue';
